import React, { ChangeEventHandler, useState } from "react";

import {
  faGift,
  faTriangleExclamation,
  faWineGlass,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  ActionTable,
  ActionTableProps,
  CheckForm,
  Flex,
  Icon,
  Section,
  Tooltip,
  Typography,
} from "@samskip/frontend-components";
import { useTranslation } from "react-i18next";

import { DomesticFormTypes } from "@assets/formTypes";
import { useAppSelector } from "@assets/hooks";
import { itemConditionLabelMapper } from "@assets/utilities/labelMapperUtils";
import { DomesticCargoDetailModal } from "@components";
import DomesticService from "@services/DomesticService";
import { domesticItemConditionsState } from "@state/slices/domesticSlice";

interface CargoSectionProps {
  cargoDetails: DomesticFormTypes.CargoDetailForm[];
  errorMessage?: string;
  handleChristmasPackageToggle: ChangeEventHandler<HTMLInputElement>;
  isChristmasPackageChecked: boolean;
  isChristmasPackageAvailable: boolean;
  onRemoveCargoDetail: (id: string) => void;
  onSaveCargoDetail: (data: DomesticFormTypes.CargoDetailForm) => void;
}

const CargoSection = ({
  cargoDetails,
  errorMessage,
  handleChristmasPackageToggle,
  isChristmasPackageChecked,
  onRemoveCargoDetail,
  onSaveCargoDetail,
  isChristmasPackageAvailable,
}: CargoSectionProps) => {
  const [cargoDetailId, setCargoDetailId] = useState<string | null>(null);

  const itemConditions = useAppSelector(domesticItemConditionsState);

  const { t } = useTranslation();

  const getDefaultDomesticCargoDetailFormWithId = () => {
    const domesticCargoFormWithId: DomesticFormTypes.CargoDetailForm = {
      ...DomesticFormTypes.defaultCargoDetailForm,
      id: self.crypto.randomUUID(),
      Description: isChristmasPackageChecked
        ? t("LABEL_CHRISTMAS_PACKAGE")
        : DomesticFormTypes.defaultCargoDetailForm.Description,
    };
    return domesticCargoFormWithId;
  };

  const searchHazardousMaterial = (searchString: string) =>
    DomesticService.searchHazardousMaterial(searchString);

  const cargoTableHeaders: ActionTableProps<DomesticFormTypes.CargoDetailForm>["columns"] =
    [
      {
        id: "description",
        title: t("LABEL_CARGO_DESCRIPTION"),
        dataIndex: "Description",
      },
      {
        id: "type",
        title: t("LABEL_TYPE"),
        className: "w-[8.75rem]",
        dataIndex: "Type",
        render: (cargoLine) =>
          cargoLine.Type ? itemConditionLabelMapper(cargoLine.Type) : "N/A",
      },
      {
        id: "quantity",
        title: `${t("LABEL_QUANTITY")} (PCS)`,
        className: "w-[7.375rem]",
        dataIndex: "Quantity",
      },
      {
        id: "weight",
        title: `${t("LABEL_WEIGHT")} (KG)`,
        className: "w-[9.375rem]",
        dataIndex: "Weight",
        render: (cargoLine) =>
          isChristmasPackageChecked ? "< 20 kg" : cargoLine.Weight ?? "N/A",
      },
      {
        id: "special",
        title: "",
        className: "w-32",
        render: (cargoLine) =>
          getSpecialHandlingIcons(cargoLine.Hazardous, cargoLine.Fragile),
      },
    ];

  const getSpecialHandlingIcons = (
    isHazardous: boolean,
    isFragile: boolean
  ) => (
    <div className="grid grid-cols-2 gap-2">
      {isHazardous && (
        <Tooltip
          className="flex justify-center"
          content={t("LABEL_HAZARDOUS_MATERIALS")}
        >
          <FontAwesomeIcon
            className="text-base text-neutral-700"
            icon={faTriangleExclamation}
          />
        </Tooltip>
      )}
      {isFragile && (
        <Tooltip
          className="col-start-2 flex justify-center"
          content={t("LABEL_FRAGILE")}
        >
          <FontAwesomeIcon
            className="text-base text-neutral-700"
            icon={faWineGlass}
          />
        </Tooltip>
      )}
    </div>
  );

  const openCargoDetailModal = (
    index: string = DomesticFormTypes.defaultCargoDetailForm.id
  ) => {
    setCargoDetailId(index);
  };

  const closeCargoDetailModal = () => {
    setCargoDetailId(null);
  };

  const handleSave = (data: DomesticFormTypes.CargoDetailForm) => {
    if (cargoDetailId !== null) {
      onSaveCargoDetail(data);
    }
    closeCargoDetailModal();
  };

  return (
    <Section id="cargoDetails" title={t("LABEL_CARGO")}>
      {isChristmasPackageAvailable && (
        <Flex className="bg-success-light p-4" gap="small" vertical>
          <CheckForm
            checked={isChristmasPackageChecked}
            fontWeight="semibold"
            id="christmas-present"
            label={
              <Flex alignItems="baseline" gap={1} justifyContent="center">
                <Icon icon={faGift} />
                {t("LABEL_CHRISTMAS_PACKAGE")}
              </Flex>
            }
            onChange={handleChristmasPackageToggle}
          />
          <Typography color="neutral-800" variant="body-s">
            {t("INFO_CHRISTMAS_GIFT")}
          </Typography>
        </Flex>
      )}
      <ActionTable
        actionButtonLabel={t("LABEL_ADD_CARGO")}
        canAdd
        canEdit
        canRemove
        columns={cargoTableHeaders}
        errorMessage={errorMessage}
        getRowId={(cargo) => cargo.id}
        id="cargoDetails"
        noDataMessage={t("LABEL_NO_CARGO")}
        onAction={() => openCargoDetailModal()}
        onEdit={openCargoDetailModal}
        onRemove={onRemoveCargoDetail}
        rows={cargoDetails}
      />

      {cargoDetailId !== null && (
        <DomesticCargoDetailModal
          isChristmasPackageChecked={isChristmasPackageChecked}
          itemConditions={itemConditions}
          onModalClose={closeCargoDetailModal}
          onSave={handleSave}
          packageDetail={
            cargoDetailId === DomesticFormTypes.defaultCargoDetailForm.id
              ? getDefaultDomesticCargoDetailFormWithId()
              : cargoDetails.find((c) => c.id === cargoDetailId)
          }
          searchHazardousMaterial={searchHazardousMaterial}
        />
      )}
    </Section>
  );
};

export default CargoSection;
